// ═══════════════════════════════════════════════════════════════════
// AdminUI.jsx — Interface administrateur (messagerie, planning, RH)
// Composants : AdminChat, VueGen
// SecRH et StaffForm → SecRH.jsx
// ═══════════════════════════════════════════════════════════════════
import React from 'react';
import { M } from './M.jsx'
import { Ic } from './Ic.jsx'
import {
  PointageTcEditor, PointageAlsCreator, PointageAlsEditor, AdminEditBadge,
} from './Pointage.jsx'
import { WeekPlan } from './TaskForm.jsx'
import { generateHistoryPdf, generatePeriodePdf, generateAdminEditsPdf } from '../pdf-utils.js'
import { TT, SUPA_KEY, SUPA_URL} from "../constants.js"
import {
  today, now, ft, fd, fdur, fm, feur, fname, effSt,
  getBreaks, totalBreakMs, pauseStatus, leaveHistoryEntry, guideImposition,
  rappelsBfEnRetard, RAPPEL_FREQS, periodeKey, formatFileSize,
} from '../utils.js'
import { supaGet, supaSet, supaAuthHeader, uploadMsgAttachment } from '../supabase.js'
import { LeaveForm } from "./RHTerrain.jsx";
import { notifEventBus } from "../notifBus.js";
import { MSG_ATT_MAX_SIZE, MSG_ATT_MAX_COUNT } from "../constants.js";

const { useState, useEffect, useRef, useMemo, Fragment} = React

// ══════════════ MESSAGERIE ADMIN ══════════════
function AdminChat({msgs,setMsgs,msgsLu,setMsgsLu,staff,prov,clients,gerants,st,pj,biens}){
  const[sel,setSel]=useState(null);
  const[inp,setInp]=useState('');
  const[pending,setPending]=useState([]);
  const fileRef=useRef(null);
  const ref=useRef(null);
  const addFiles=(files)=>{
    if(!sel)return;
    const list=Array.from(files||[]);
    if(!list.length)return;
    setPending(prev=>{
      const room=MSG_ATT_MAX_COUNT-prev.length;
      if(room<=0)return prev;
      const toAdd=list.slice(0,room).map(file=>{
        const id='p'+Date.now()+Math.random().toString(36).slice(2,7);
        if(file.size>MSG_ATT_MAX_SIZE)return{id,file,status:'err',err:'Trop volumineux (max 15 Mo)'};
        return{id,file,status:'up'};
      });
      toAdd.filter(p=>p.status==='up').forEach(p=>{
        uploadMsgAttachment(p.file,sel)
          .then(result=>setPending(prev2=>prev2.map(x=>x.id===p.id?{...x,status:'ok',result}:x)))
          .catch(e=>setPending(prev2=>prev2.map(x=>x.id===p.id?{...x,status:'err',err:e.message||'Échec de l\'envoi'}:x)));
      });
      return[...prev,...toAdd];
    });
  };
  const removePending=id=>setPending(prev=>prev.filter(x=>x.id!==id));
  // luSet : IDs des messages lus par l'admin (clé séparée, robuste multi-session)
  const luSet=useMemo(()=>new Set(msgsLu||[]),[msgsLu]);
  const markLu=(...ids)=>setMsgsLu(prev=>{const s=new Set(prev||[]);ids.forEach(id=>s.add(id));return Array.from(s);});
  const threads=useMemo(()=>{
    const all=[
      ...staff.filter(s=>!s.archived||msgs.some(m=>m.tid===`staff:${s.id}`&&!luSet.has(m.id)&&m.ft!=='bot'&&m.ft!=='admin')).map(s=>({id:`staff:${s.id}`,nm:fname(s),sb:s.role,ac:'var(--mn)',archived:!!s.archived})),
      ...prov.filter(p=>!p.archived||msgs.some(m=>m.tid===`provider:${p.id}`&&!luSet.has(m.id)&&m.ft!=='bot'&&m.ft!=='admin')).map(p=>({id:`provider:${p.id}`,nm:p.name,sb:p.type,ac:'var(--or)',archived:!!p.archived})),
      ...(gerants||[]).filter(g=>!g.archived||msgs.some(m=>m.tid===`gerant:${g.id}`&&!luSet.has(m.id)&&m.ft!=='bot'&&m.ft!=='admin')).map(g=>({id:`gerant:${g.id}`,nm:g.name||fname(g)||g.email||'Direction',sb:'Direction',ac:'var(--bl)',archived:!!g.archived})),
      ...clients.map(c=>({id:`client:${c.id}`,nm:c.name,sb:'Propriétaire',ac:'var(--pk)',archived:false}))
    ];
    return all.map(t=>{const tm=msgs.filter(m=>m.tid===t.id);const last=tm.sort((a,b)=>new Date(b.ts)-new Date(a.ts))[0];const ur=tm.filter(m=>!luSet.has(m.id)&&m.ft!=='bot'&&m.ft!=='admin').length;const es=tm.some(m=>m.esc&&!luSet.has(m.id));return{...t,last,ur,es};}).sort((a,b)=>b.ur-a.ur);
  },[msgs,staff,prov,gerants,clients]);
  const cur=threads.find(t=>t.id===sel);
  const cMsgs=sel?msgs.filter(m=>m.tid===sel).sort((a,b)=>new Date(a.ts)-new Date(b.ts)):[];
  // Corps en accolades obligatoire — cf. explication détaillée dans SimpleChat.jsx :
  // un corps implicite renvoie le résultat de scrollIntoView à React comme fonction de nettoyage.
  useEffect(()=>{ ref.current?.scrollIntoView({behavior:'smooth'}); },[cMsgs.length]);
  useEffect(()=>{if(!sel)return;const unread=msgs.filter(m=>m.tid===sel&&!luSet.has(m.id)&&m.ft!=='bot'&&m.ft!=='admin');if(unread.length)markLu(...unread.map(m=>m.id));},[sel]);
  useEffect(()=>{setPending([]);},[sel]);
  const attOk=pending.filter(p=>p.status==='ok').map(p=>p.result);
  const hasUploading=pending.some(p=>p.status==='up');
  const send=()=>{
    if(hasUploading)return;
    if((!inp.trim()&&attOk.length===0)||!sel)return;
    const ct=inp.trim();
    setMsgs([...msgs,{id:'m'+Date.now(),tid:sel,ft:'admin',fid:'admin',ct,ts:now(),ra:true,ru:false,...(attOk.length?{att:attOk}:{})}]);
    // ─── notif : message_recu (admin → quelqu'un) ───
    const[toUserType,toUserId]=sel.split(':');
    notifEventBus.emit('message_recu',{
      fromName:'Admin', fromType:'admin',
      toUserType, toUserId,
      body:ct||`📎 ${attOk.length} pièce(s) jointe(s)`,
      summary:`Message de Admin → ${cur?.nm||'?'}`
    });
    setInp('');setPending([]);
  };
  const tu=threads.reduce((s,t)=>s+t.ur,0);
  return(<div>
    <div style={{fontSize:12,color:'var(--si)',marginBottom:14}}>{tu>0?`${tu} message(s) non lu(s)`:'Tout à jour ✦'}</div>
    <div className="cgrid">
      <div style={{borderRight:'1px solid var(--li)',background:'#fbf5ec',overflowY:'auto',maxHeight:480}}>
        {threads.map(t=>(<div key={t.id} onClick={()=>setSel(t.id)} className={`citem ${sel===t.id?'on':''}`} style={{borderLeft:t.ur>0?`3px solid ${t.ac}`:'3px solid transparent'}}>
          <div style={{display:'flex',alignItems:'center',gap:5,marginBottom:2}}><span style={{width:7,height:7,borderRadius:'50%',background:t.ac,display:'inline-block'}}/><span style={{fontWeight:600,fontSize:12,flex:1,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{t.nm}</span>{t.archived&&<span style={{fontSize:9,color:'var(--si)',border:'1px solid var(--ls)',borderRadius:4,padding:'0 4px'}}>archivé</span>}{t.ur>0&&<span className="bc">{t.ur}</span>}</div>
          <div style={{fontSize:11,color:'var(--si)',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{t.last?(t.last.ct||(t.last.att&&t.last.att.length?`📎 ${t.last.att.length} pièce(s) jointe(s)`:'—')):'—'}</div>
          {t.es&&<div style={{fontSize:10,color:'var(--or)',fontWeight:600,marginTop:2}}>⚠ Escaladé</div>}
        </div>))}
      </div>
      <div style={{display:'flex',flexDirection:'column'}}>
        {!cur?<div className="empty" style={{flex:1,display:'flex',alignItems:'center',justifyContent:'center'}}>Sélectionne une conversation</div>:(
          <Fragment>
            <div style={{padding:'11px 14px',borderBottom:'1px solid var(--li)',borderTop:`3px solid ${cur.ac}`,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
              <div><div className="D" style={{fontSize:14}}>{cur.nm}</div><div style={{fontSize:11,color:'var(--si)'}}>{cur.sb}</div></div>
            </div>
            <div style={{flex:1,overflowY:'auto',padding:12,background:'var(--bg)',display:'flex',flexDirection:'column',gap:8,maxHeight:340}}>
              {cMsgs.map((m,idx)=>{
                const isA=m.ft==='admin',isB=m.ft==='bot';
                // ─── Affichage groupé du marqueur "Escaladé" ───
                // Ne s'affiche que pour le PREMIER message escaladé d'une série consécutive
                // du même expéditeur. Si le précédent était aussi esc et du même fid, on saute.
                const prev=idx>0?cMsgs[idx-1]:null;
                const showEscBadge=m.esc&&!isA&&!isB&&!(prev&&prev.esc&&prev.fid===m.fid&&prev.ft===m.ft);
                return(<Fragment key={m.id}>{showEscBadge&&<div style={{background:'#fff4d6',border:'1px solid var(--ye)',borderRadius:7,padding:'5px 9px',fontSize:11,color:'#6b5210'}}>⚠ Escaladé</div>}<div className={`bbl ${isA?'bme':isB?'bbt':'bth'}`}>{m.ct&&<div style={{whiteSpace:'pre-wrap'}}>{m.ct}</div>}{m.att&&m.att.length>0&&<div style={{display:'flex',flexDirection:'column',gap:6,marginTop:m.ct?6:0}}>{m.att.map((a,i)=>a.type&&a.type.startsWith('image/')?<a key={i} href={a.url} target="_blank" rel="noopener noreferrer"><img src={a.url} alt={a.name||'photo'} style={{maxWidth:180,maxHeight:180,borderRadius:8,display:'block'}}/></a>:<a key={i} href={a.url} target="_blank" rel="noopener noreferrer" style={{display:'flex',alignItems:'center',gap:6,fontSize:11,padding:'6px 9px',background:'rgba(0,0,0,.06)',borderRadius:7,textDecoration:'none',color:'inherit'}}><Ic n="rp" c="w-4 h-4"/><span style={{overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',maxWidth:140}}>{a.name}</span>{a.size?<span style={{opacity:.6}}>{formatFileSize(a.size)}</span>:null}</a>)}</div>}{!isA&&!isB&&m.fName&&<div style={{fontSize:9,opacity:.65,marginTop:3,textAlign:'right'}}>— {m.fName}</div>}</div></Fragment>);
              })}
              <div ref={ref}/>
            </div>
            {pending.length>0&&<div style={{display:'flex',flexWrap:'wrap',gap:6,padding:'8px 12px 0'}}>
              {pending.map(p=><div key={p.id} style={{display:'flex',alignItems:'center',gap:5,fontSize:11,padding:'4px 8px',borderRadius:999,background:p.status==='err'?'#fde0e0':'var(--bg)',border:'1px solid var(--li)'}}>
                <span style={{maxWidth:100,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{p.file.name}</span>
                {p.status==='up'&&<span style={{opacity:.6}}>…</span>}
                {p.status==='err'&&<span style={{color:'var(--dg)'}} title={p.err}>⚠</span>}
                {p.status==='ok'&&<span style={{color:'var(--ok)'}}>✓</span>}
                <button onClick={()=>removePending(p.id)} type="button" style={{border:'none',background:'none',cursor:'pointer',padding:0,lineHeight:1,color:'inherit'}}><Ic n="x" c="w-3 h-3"/></button>
              </div>)}
            </div>}
            <div style={{padding:'9px 12px',borderTop:'1px solid var(--li)',background:'var(--wh)',display:'flex',gap:6,alignItems:'flex-end'}}>
              <input ref={fileRef} type="file" multiple accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,.txt" style={{display:'none'}} onChange={e=>{addFiles(e.target.files);e.target.value='';}}/>
              <button onClick={()=>fileRef.current?.click()} type="button" className="btn bor sm" title="Joindre un fichier"><Ic n="atc" c="w-4 h-4"/></button>
              <textarea value={inp} onChange={e=>setInp(e.target.value)} onKeyDown={e=>{if(e.key==='Enter'&&!e.shiftKey){e.preventDefault();send();}}} rows="1" placeholder={`Réponds à ${cur.nm}...`} style={{resize:'none',minHeight:34,maxHeight:70,fontSize:13}}/>
              <button onClick={send} className="btn bor sm" disabled={(!inp.trim()&&attOk.length===0)||hasUploading}><Ic n="sd" c="w-4 h-4"/></button>
            </div>
          </Fragment>
        )}
      </div>
    </div>
  </div>);
}

// ══════════════ VUE GÉNÉRALE ══════════════

const CANI_NIVEAUX = [
  {k:'vert',   label:'Vert — Normal',   color:'#2e7d32', bg:'#e8f5e9', emoji:'🟢', desc:'Conditions normales'},
  {k:'jaune',  label:'Jaune — Vigilance',color:'#f57f17',bg:'#fffde7', emoji:'🟡', desc:'Chaleur modérée — hydratation renforcée'},
  {k:'orange', label:'Orange — Alerte', color:'#e65100', bg:'#fff3e0', emoji:'🟠', desc:'Forte chaleur — pauses obligatoires'},
  {k:'rouge',  label:'Rouge — Urgence', color:'#b71c1c', bg:'#ffebee', emoji:'🔴', desc:'Canicule extrême — mesures maximales'},
];

const CANI_MESURES=[
  {k:'eau',      l:'💧 Eau fraîche distribuée'},
  {k:'pauses',   l:'⏸️ Pauses renforcées'},
  {k:'douches',  l:'🚿 Douches autorisées sur site'},
  {k:'horaires', l:'⏰ Horaires décalés (éviter 12h-17h)'},
  {k:'ventil',   l:'🌬️ Ventilation / clim vérifiée'},
  {k:'binome',   l:'👥 Travail en binôme / contact renforcé'},
  {k:'arret',    l:'🛑 Tâches extérieures suspendues'},
];

function CaniPilotage(){
  const[niveau,setNiveau]=useState(null);
  const[msg,setMsg]=useState('');
  const[loaded,setLoaded]=useState(false);
  const[saving,setSaving]=useState(false);
  const[open,setOpen]=useState(false);
  const niveauRef=useRef(null);

  // Prévision météo (Open-Meteo, sans clé)
  const[meteo,setMeteo]=useState(null); // {tmax, tmin, niveauSuggere, fetchedAt}
  const[loadingMeteo,setLoadingMeteo]=useState(false);

  const fetchMeteo=async()=>{
    setLoadingMeteo(true);
    try{
      const r=await fetch('https://api.open-meteo.com/v1/forecast?latitude=48.08&longitude=7.36&daily=temperature_2m_max,temperature_2m_min&timezone=Europe%2FParis&forecast_days=1');
      const d=await r.json();
      const tmax=Math.round(d.daily.temperature_2m_max[0]);
      const tmin=Math.round(d.daily.temperature_2m_min[0]);
      let niveauSuggere='vert';
      if(tmax>=40) niveauSuggere='rouge';
      else if(tmax>=36) niveauSuggere='orange';
      else if(tmax>=32) niveauSuggere='jaune';
      const m={tmax,tmin,niveauSuggere,fetchedAt:new Date().toLocaleTimeString('fr-FR',{hour:'2-digit',minute:'2-digit'})};
      setMeteo(m);
      // Sauvegarder dans canicule7 pour la lecture par l'Edge Function
      const current=await supaGet('canicule7').catch(()=>null);
      await supaSet('canicule7',{...(current||{}),niveauMeteo:niveauSuggere,tmaxPrev:tmax,tminPrev:tmin,meteoUpdatedAt:now()}).catch(()=>{});
    }catch(e){console.error('Météo:',e);}
    setLoadingMeteo(false);
  };

  // Carnet de bord quotidien
  const[carnet,setCarnet]=useState({});       // {date: {mesures:[],note:'',niveauJour:'',ts:''}}
  const[showCarnet,setShowCarnet]=useState(false);
  const[carnetDate,setCarnetDate]=useState(today());
  const[carnetMesures,setCarnetMesures]=useState([]);
  const[carnetNote,setCarnetNote]=useState('');
  const[carnetNiveauJour,setCarnetNiveauJour]=useState('');
  const[savingCarnet,setSavingCarnet]=useState(false);

  useEffect(()=>{
    supaGet('canicule7').then(d=>{
      if(d){
        setNiveau(d.niveau||null);niveauRef.current=d.niveau||null;setMsg(d.message||'');
        // Restaurer la dernière prévision si dispo
        if(d.tmaxPrev!=null) setMeteo({tmax:d.tmaxPrev,tmin:d.tminPrev,niveauSuggere:d.niveauMeteo||'vert',fetchedAt:'(dernière synchro)'});
      }
      setLoaded(true);
    }).catch(()=>setLoaded(true));
    supaGet('caniCarnet7').then(d=>{ if(d&&typeof d==='object') setCarnet(d); }).catch(()=>{});
    fetchMeteo();
  },[]);

  // Quand on change de date dans le carnet, charger l'entrée existante
  useEffect(()=>{
    const entry=carnet[carnetDate];
    setCarnetMesures(entry?.mesures||[]);
    setCarnetNote(entry?.note||'');
    setCarnetNiveauJour(entry?.niveauJour||'');
  },[carnetDate,carnet]);

  const save=async(nv,m)=>{
    setSaving(true);
    try{await supaSet('canicule7',{niveau:nv,message:m,updatedAt:now()});}catch(e){console.error(e);}
    setNiveau(nv);niveauRef.current=nv;setMsg(m);setSaving(false);
  };
  const desactiver=async()=>{
    setSaving(true);
    try{await supaSet('canicule7',{niveau:null,message:'',updatedAt:now()});}catch(e){console.error(e);}
    setNiveau(null);niveauRef.current=null;setMsg('');setSaving(false);
  };

  const saveCarnet=async()=>{
    setSavingCarnet(true);
    const updated={...carnet,[carnetDate]:{mesures:carnetMesures,note:carnetNote,niveauJour:carnetNiveauJour,ts:now()}};
    try{await supaSet('caniCarnet7',updated);setCarnet(updated);}catch(e){console.error(e);}
    setSavingCarnet(false);
  };

  const exportCarnet=()=>{
    const lines=['CARNET DE BORD CANICULE — La Conciergerie','Exporté le '+new Date().toLocaleString('fr-FR'),''];
    const dates=Object.keys(carnet).sort().reverse();
    dates.forEach(date=>{
      const e=carnet[date];
      const nObj=CANI_NIVEAUX.find(n=>n.k===e.niveauJour);
      lines.push('─── '+new Date(date+'T12:00').toLocaleDateString('fr-FR',{weekday:'long',day:'numeric',month:'long',year:'numeric'})+' ───');
      if(nObj) lines.push('Niveau : '+nObj.label);
      if(e.mesures?.length) lines.push('Mesures prises :');
      (e.mesures||[]).forEach(m=>{const mm=CANI_MESURES.find(x=>x.k===m);if(mm)lines.push('  ✓ '+mm.l.replace(/^\S+\s/,''));});
      if(e.note) lines.push('Notes : '+e.note);
      lines.push('');
    });
    const blob=new Blob([lines.join('\n')],{type:'text/plain;charset=utf-8'});
    const url=URL.createObjectURL(blob);
    const a=document.createElement('a');a.href=url;
    a.download='carnet_canicule_'+today()+'.txt';
    document.body.appendChild(a);a.click();
    setTimeout(()=>{document.body.removeChild(a);URL.revokeObjectURL(url);},1000);
  };

  const toggleMesure=(k)=>setCarnetMesures(prev=>prev.includes(k)?prev.filter(x=>x!==k):[...prev,k]);
  const nbjours=Object.keys(carnet).length;
  const entryJour=carnet[carnetDate];
  const niv=CANI_NIVEAUX.find(n=>n.k===niveau);
  const inactive=!niv;
  return(<div style={{marginBottom:16}}>
    <div onClick={()=>setOpen(o=>!o)} style={{background:inactive?'#e3f2fd':niv.bg,border:`2px solid ${inactive?'#1976D2':niv.color}`,borderRadius:12,padding:'12px 16px',cursor:'pointer',display:'flex',alignItems:'center',gap:12,justifyContent:'space-between'}}>
      <div style={{display:'flex',alignItems:'center',gap:10}}>
        <div style={{fontSize:22}}>{inactive?'🌡️':niv.emoji}</div>
        <div>
          <div style={{fontWeight:700,fontSize:13,color:inactive?'#1565C0':niv.color}}>🌡️ Pilotage canicule</div>
          <div style={{fontSize:12,color:inactive?'#1976D2':niv.color}}>{inactive?'Non activé — cliquer pour définir le niveau du jour':niv.label}</div>
        </div>
      </div>
      <div style={{fontSize:12,color:inactive?'#1976D2':'var(--si)',fontWeight:600}}>{open?'▲ Fermer':'▼ Ouvrir'}</div>
    </div>
    {open&&loaded&&(<div style={{background:'var(--cr)',border:'1.5px solid #ddd',borderRadius:'0 0 12px 12px',borderTop:'none',padding:'14px 16px'}}>
      {/* ── Prévision météo ── */}
      {(()=>{
        const mNiv=meteo?CANI_NIVEAUX.find(n=>n.k===meteo.niveauSuggere):null;
        return(<div style={{background:'#f0f4ff',border:'1.5px solid #c7d7f9',borderRadius:10,padding:'10px 12px',marginBottom:14}}>
          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:8,flexWrap:'wrap'}}>
            <div style={{display:'flex',alignItems:'center',gap:8}}>
              <span style={{fontSize:16}}>📡</span>
              <div>
                <div style={{fontSize:11,fontWeight:700,color:'#1e3a8a'}}>Prévision Colmar (Open-Meteo)</div>
                {meteo
                  ?<div style={{fontSize:12,color:'#374151'}}>
                    <span style={{fontWeight:700}}>{meteo.tmax}°C</span> max · <span style={{fontWeight:600}}>{meteo.tmin}°C</span> min
                    {mNiv&&<span style={{marginLeft:8,fontWeight:700,color:mNiv.color}}>{mNiv.emoji} {mNiv.label.split('—')[0].trim()} suggéré</span>}
                    <span style={{fontSize:10,color:'#9ca3af',marginLeft:6}}>{meteo.fetchedAt}</span>
                  </div>
                  :<div style={{fontSize:11,color:'#6b7280'}}>{loadingMeteo?'Chargement…':'—'}</div>
                }
              </div>
            </div>
            <div style={{display:'flex',gap:6,alignItems:'center'}}>
              {meteo&&meteo.niveauSuggere!=='vert'&&meteo.niveauSuggere!==niveau&&(
                <button onClick={()=>{setNiveau(meteo.niveauSuggere);if(mNiv)setMsg(meteo.niveauSuggere==='orange'?'Vigilance orange active. Hydratez-vous toutes les 30 min sans attendre d\'avoir soif. Portez une casquette et de la crème solaire entre les appartements. Activez la climatisation dès votre arrivée dans chaque logement — vous êtes autorisée à prendre une douche rapide si vous en ressentez le besoin. Évitez les efforts intenses entre 12h et 17h, décalez si possible. En cas de malaise, appuyez immédiatement sur le bouton 🥵 dans l\'appli.':meteo.niveauSuggere==='rouge'?'Canicule extrême — mesures maximales en vigueur. Hydratation obligatoire toutes les 20 min. Aucun déplacement extérieur sans casquette et crème solaire. Dans chaque appartement : clim allumée immédiatement, douche autorisée et encouragée si besoin. Pas de ménage seule entre 12h et 17h sans accord préalable — contactez Claire ou Franck avant de commencer. Au moindre signe de malaise (vertiges, maux de tête, fatigue soudaine), arrêtez immédiatement et appuyez sur le bouton 🥵.':'Chaleur modérée aujourd\'hui. Pensez à boire régulièrement sans attendre d\'avoir soif, au moins un verre d\'eau toutes les heures. Portez des vêtements légers. Entre deux appartements, restez à l\'ombre autant que possible. Signalez tout signe de fatigue inhabituelle.');}}
                className="btn bmn sm" style={{fontSize:11,whiteSpace:'nowrap'}}>
                  ↓ Appliquer {mNiv?.emoji}
                </button>
              )}
              <button onClick={fetchMeteo} disabled={loadingMeteo} className="btn bgh sm" style={{fontSize:11}}>{loadingMeteo?'…':'🔄'}</button>
            </div>
          </div>
          <div style={{fontSize:10,color:'#9ca3af',marginTop:5}}>Estimation basée sur les seuils Météo-France Alsace. Vérifier vigilance.meteofrance.fr en cas de doute.</div>
        </div>);
      })()}
      <div style={{fontSize:12,fontWeight:700,marginBottom:10,color:'var(--si)',textTransform:'uppercase',letterSpacing:'.05em'}}>Niveau du jour</div>
      <div style={{display:'grid',gridTemplateColumns:'repeat(2,1fr)',gap:8,marginBottom:14}}>
        {CANI_NIVEAUX.map(n=>(<button key={n.k} onClick={()=>setNiveau(n.k)} style={{padding:'10px 8px',borderRadius:10,border:`2px solid ${niveau===n.k?n.color:'#ddd'}`,background:niveau===n.k?n.bg:'white',cursor:'pointer',textAlign:'left',transition:'all .15s'}}>
          <div style={{fontSize:18,marginBottom:3}}>{n.emoji}</div>
          <div style={{fontSize:12,fontWeight:700,color:n.color}}>{n.label}</div>
          <div style={{fontSize:10,color:'var(--si)'}}>{n.desc}</div>
        </button>))}
      </div>
      <div style={{fontSize:12,fontWeight:700,marginBottom:6,color:'var(--si)',textTransform:'uppercase',letterSpacing:'.05em'}}>Message aux équipes (optionnel)</div>
      <textarea value={msg} onChange={e=>setMsg(e.target.value)} placeholder="Ex : Pensez à vous hydrater toutes les 30 min. Pauses obligatoires entre 12h et 14h..." style={{width:'100%',minHeight:70,borderRadius:8,border:'1.5px solid #ddd',padding:'8px 10px',fontSize:12,fontFamily:'inherit',resize:'vertical',boxSizing:'border-box',marginBottom:12}}/>
      <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
        <button onClick={()=>save(niveau,msg)} disabled={saving||!niveau} className="btn bmn sm" style={{flex:1}}>{saving?'…':'✓ Activer ce niveau'}</button>
        {niveau&&<button onClick={desactiver} disabled={saving} className="btn bgh sm">✕ Désactiver</button>}
      </div>
      {niveau&&<div style={{marginTop:10,fontSize:11,color:'var(--si)'}}>Ce bandeau s'affichera immédiatement pour toute l'équipe terrain dans leur onglet Pointage.</div>}

      {/* ── Carnet de bord DUERP ── */}
      <div style={{marginTop:16,borderTop:'1px solid #ddd',paddingTop:12}}>
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:8}}>
          <button onClick={()=>setShowCarnet(v=>!v)} style={{background:'none',border:'none',cursor:'pointer',fontSize:12,fontWeight:700,color:'var(--si)',padding:0,display:'flex',alignItems:'center',gap:6}}>
            📓 Carnet de bord DUERP {nbjours>0&&<span style={{background:'#e3f2fd',color:'#1565C0',borderRadius:999,padding:'1px 7px',fontSize:11}}>{nbjours} jour{nbjours>1?'s':''}</span>} {showCarnet?'▲':'▼'}
          </button>
          {nbjours>0&&<button onClick={exportCarnet} className="btn bgh sm" style={{fontSize:11}}>📥 Exporter</button>}
        </div>
        {showCarnet&&(<div>
          {/* Sélecteur de date */}
          <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:12}}>
            <label style={{fontSize:11,fontWeight:700,color:'var(--si)',whiteSpace:'nowrap'}}>Date :</label>
            <input type="date" value={carnetDate} onChange={e=>setCarnetDate(e.target.value)}
              style={{border:'1.5px solid #ddd',borderRadius:7,padding:'4px 8px',fontSize:12,flex:1}}/>
            {entryJour&&<span style={{fontSize:10,color:'var(--ok)',fontWeight:700,whiteSpace:'nowrap'}}>✓ entrée existante</span>}
          </div>
          {/* Niveau de ce jour */}
          <div style={{fontSize:11,fontWeight:700,color:'var(--si)',marginBottom:6}}>Niveau de vigilance ce jour</div>
          <div style={{display:'flex',gap:6,flexWrap:'wrap',marginBottom:12}}>
            {CANI_NIVEAUX.map(n=>(<button key={n.k} onClick={()=>setCarnetNiveauJour(v=>v===n.k?'':n.k)}
              style={{padding:'4px 10px',borderRadius:999,border:`1.5px solid ${carnetNiveauJour===n.k?n.color:'#ddd'}`,background:carnetNiveauJour===n.k?n.bg:'white',cursor:'pointer',fontSize:11,fontWeight:600,color:n.color}}>
              {n.emoji} {n.label.split('—')[0].trim()}
            </button>))}
          </div>
          {/* Mesures prises */}
          <div style={{fontSize:11,fontWeight:700,color:'var(--si)',marginBottom:6}}>Mesures prises ce jour</div>
          <div style={{display:'flex',flexDirection:'column',gap:6,marginBottom:12}}>
            {CANI_MESURES.map(m=>(<label key={m.k} style={{display:'flex',alignItems:'center',gap:8,cursor:'pointer',fontSize:12}}>
              <input type="checkbox" checked={carnetMesures.includes(m.k)} onChange={()=>toggleMesure(m.k)}
                style={{width:15,height:15,accentColor:'#1976D2',cursor:'pointer',flexShrink:0}}/>
              <span style={{color:carnetMesures.includes(m.k)?'var(--ink)':'var(--si)'}}>{m.l}</span>
            </label>))}
          </div>
          {/* Note libre */}
          <div style={{fontSize:11,fontWeight:700,color:'var(--si)',marginBottom:4}}>Notes / observations</div>
          <textarea value={carnetNote} onChange={e=>setCarnetNote(e.target.value)}
            placeholder="Ex : Gaella a signalé une fatigue inhabituelle à 14h — pause accordée 20 min. Ménage du studio 1615 décalé à 9h30."
            style={{width:'100%',minHeight:65,borderRadius:8,border:'1.5px solid #ddd',padding:'7px 10px',fontSize:12,fontFamily:'inherit',resize:'vertical',boxSizing:'border-box',marginBottom:10}}/>
          <button onClick={saveCarnet} disabled={savingCarnet} className="btn bmn sm" style={{width:'100%'}}>
            {savingCarnet?'…':'💾 Enregistrer cette journée'}
          </button>
          {/* Historique compact */}
          {nbjours>0&&(<div style={{marginTop:14}}>
            <div style={{fontSize:11,fontWeight:700,color:'var(--si)',marginBottom:6,textTransform:'uppercase',letterSpacing:'.05em'}}>Historique</div>
            {Object.keys(carnet).sort().reverse().slice(0,10).map(date=>{
              const e=carnet[date];
              const nObj=CANI_NIVEAUX.find(n=>n.k===e.niveauJour);
              return(<div key={date} onClick={()=>setCarnetDate(date)} style={{padding:'7px 10px',borderRadius:8,border:'1px solid #eee',marginBottom:5,cursor:'pointer',background:date===carnetDate?'#e3f2fd':'white'}}>
                <div style={{display:'flex',gap:8,alignItems:'center',flexWrap:'wrap'}}>
                  <span style={{fontSize:11,fontWeight:700}}>{new Date(date+'T12:00').toLocaleDateString('fr-FR',{weekday:'short',day:'numeric',month:'short'})}</span>
                  {nObj&&<span style={{fontSize:10,color:nObj.color,fontWeight:600}}>{nObj.emoji} {nObj.label.split('—')[0].trim()}</span>}
                  {e.mesures?.length>0&&<span style={{fontSize:10,color:'var(--ok)'}}>✓ {e.mesures.length} mesure{e.mesures.length>1?'s':''}</span>}
                </div>
                {e.note&&<div style={{fontSize:10,color:'var(--si)',marginTop:2,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{e.note}</div>}
              </div>);
            })}
            {nbjours>10&&<div style={{fontSize:11,color:'var(--si)',textAlign:'center',marginTop:4}}>… {nbjours-10} jours plus anciens (voir export)</div>}
          </div>)}
        </div>)}
      </div>
    </div>)}
  </div>);
}


function CommentairesView({commentaires,setCommentaires,biens,staff}){
  const[filBien,setFilBien]=React.useState('all');
  const comFiltres=React.useMemo(()=>[...(commentaires||[])].filter(c=>c.texte&&(filBien==='all'||c.bid===filBien)).sort((a,b)=>b.ts.localeCompare(a.ts)),[commentaires,filBien]);
  const biensAvecCom=React.useMemo(()=>[...new Map((commentaires||[]).filter(c=>c.texte).map(c=>[c.bid,biens.find(x=>x.id===c.bid)])).values()].filter(Boolean).sort((a,b)=>a.name.localeCompare(b.name)),[commentaires,biens]);
  return(<div>
    <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:14,flexWrap:'wrap',gap:8}}>
      <div className="D" style={{fontSize:18}}>💬 Commentaires d'intervention</div>
      {(commentaires||[]).some(c=>!c.lu)&&<button onClick={()=>setCommentaires((commentaires||[]).map(c=>({...c,lu:true})))} className="btn bgh sm">✓ Tout marquer comme lu</button>}
    </div>
    <div style={{fontSize:11,color:'var(--si)',background:'var(--bg)',borderRadius:8,padding:'8px 11px',marginBottom:16,display:'flex',gap:6}}>
      <span>🔒</span><span>Ces commentaires sont <strong>confidentiels</strong> — non visibles sur l'interface propriétaire.</span>
    </div>
    {biensAvecCom.length>1&&<div style={{display:'flex',gap:6,flexWrap:'wrap',marginBottom:14}}>
      <button onClick={()=>setFilBien('all')} className={`btn bgh sm ${filBien==='all'?'on':''}`} style={{fontWeight:filBien==='all'?700:400}}>Tous</button>
      {biensAvecCom.map(b=><button key={b.id} onClick={()=>setFilBien(b.id)} className={`btn bgh sm ${filBien===b.id?'on':''}`} style={{fontWeight:filBien===b.id?700:400}}>{b.name}</button>)}
    </div>}
    {comFiltres.length===0
      ?<div className="empty">Aucun commentaire{filBien!=='all'?' pour cet appartement':' pour le moment'}.</div>
      :<div style={{display:'flex',flexDirection:'column',gap:8}}>
        {comFiltres.map(c=>{
          const b=biens.find(x=>x.id===c.bid);
          const s=staff.find(x=>x.id===c.sid);
          const tt=TT[c.type]||{};
          const niv={info:{label:'OK',bg:'#e1f7ee',c:'#1e6e3d',e:'✅'},attention:{label:'À noter',bg:'#fff4d6',c:'#7a5a00',e:'⚠️'},incident:{label:'Incident',bg:'#fde0e0',c:'#8a1010',e:'🔴'}}[c.niveau]||{};
          return(<div key={c.id} className="card" style={{padding:'13px 16px',borderLeft:`3px solid ${c.lu?'#9ca3af':niv.c||'var(--bl)'}`,opacity:c.lu?.6:1,background:c.lu?'var(--bg)':''}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',flexWrap:'wrap',gap:8}}>
              <div style={{flex:1}}>
                <div style={{display:'flex',gap:6,marginBottom:6,flexWrap:'wrap',alignItems:'center'}}>
                  {!c.lu&&<span style={{width:8,height:8,borderRadius:'50%',background:'var(--bl)',display:'inline-block',flexShrink:0}} title="Non lu"/>}{c.lu&&<span style={{fontSize:11,color:'var(--ok)',fontWeight:700}}>✓ Lu</span>}
                  <span style={{background:niv.bg,color:niv.c,fontSize:11,fontWeight:700,padding:'2px 8px',borderRadius:999}}>{niv.e} {niv.label}</span>
                  <span style={{background:tt.bg,color:tt.c,fontSize:11,fontWeight:600,padding:'2px 7px',borderRadius:999}}>{tt.e} {tt.l}</span>
                  <span className="D" style={{fontSize:13}}>{b?.name}</span>
                </div>
                <div style={{fontSize:11,color:'var(--si)',marginBottom:6}}>{s?.name} · {fd(c.date)} à {ft(c.ts)}</div>
                <div style={{fontSize:13,lineHeight:1.6,background:'var(--bg)',borderRadius:8,padding:'8px 11px'}}>{c.texte}</div>
              </div>
              {!c.lu&&<button onClick={()=>setCommentaires((commentaires||[]).map(x=>x.id===c.id?{...x,lu:true}:x))} className="btn bgh sm" style={{fontSize:11,flexShrink:0}}>✓ Lu</button>}
            </div>
          </div>);
        })}
      </div>
    }
  </div>);
}

function VueGen(P){
  const{st,pj,biens,setBiens,staff,prov,leave,unavail,msgs,setMsgs,msgsLu,setMsgsLu,tc,als,rep,clients,commentaires,setCommentaires,onNav,alertesSante,setAlertesSante,initialSub}=P;
  const[sub,setSub]=useState(initialSub||'db');
  React.useEffect(()=>{if(initialSub)setSub(initialSub);},[initialSub]);
  const todayS=today();
  const luSetVG=useMemo(()=>new Set(msgsLu||[]),[msgsLu]);
  const unread=msgs.filter(m=>!luSetVG.has(m.id)&&m.ft!=='bot'&&m.ft!=='admin').length;
  const unassT=pj.filter(j=>j.date===todayS&&!j.pid&&!j.risque).length+st.filter(t=>t.date===todayS&&!t.sid&&!t.risque&&t.st!=='cancelled').length;
  // Pointage manquant = a au moins une tâche du jour dont l'heure de début est DÉJÀ PASSÉE
  // (avec 15 min de tolérance) ET n'a pas encore pointé son début (sw).
  // Une tâche à venir plus tard dans la journée ne déclenche pas l'alerte.
  const _nowMin=(()=>{const d=new Date();return d.getHours()*60+d.getMinutes();})();
  const lateCl=staff.filter(s=>{
    const tachesDuJour=st.filter(t=>t.sid===s.id&&t.date===todayS);
    // au moins une tâche dont l'heure de début (sh:sm) est dépassée de plus de 15 min
    const aDebutDepasse=tachesDuJour.some(t=>{
      const deb=(Number(t.sh)||0)*60+(Number(t.sm)||0);
      return _nowMin>=deb+15;
    });
    const aPointe=tc.some(t=>t.sid===s.id&&t.date===todayS&&t.sw);
    return aDebutDepasse&&!aPointe;
  });
  const pendReq=P.req?.filter(r=>r.st==='pending').length||0;
  const nonLusCom=(commentaires||[]).filter(c=>!c.lu&&c.texte).length;
  const niveauCom=(commentaires||[]).filter(c=>!c.lu&&c.texte).reduce((mx,c)=>c.niveau==='incident'?'incident':mx==='incident'?'incident':c.niveau==='attention'?'attention':mx,'info');
  const comColor=niveauCom==='incident'?'var(--dg)':niveauCom==='attention'?'var(--or)':'var(--bl)';
  return(<div>
    <div className="stabs">
      <button className={`st ${sub==='db'?'on':''}`} onClick={()=>setSub('db')}><Ic n="ct"/>Tableau de bord</button>
      <button className={`st ${sub==='plan'?'on':''}`} onClick={()=>setSub('plan')}><Ic n="ca"/>Planning semaine</button>
      <button className={`st ${sub==='com'?'on':''}`} onClick={()=>setSub('com')}>💬 Commentaires{nonLusCom>0&&<span className="bc" style={{background:comColor}}>{nonLusCom}</span>}</button>
      <button className={`st ${sub==='msg'?'on':''}`} onClick={()=>setSub('msg')}><Ic n="ch"/>Messages{unread>0&&<span className="bc">{unread}</span>}</button>
    </div>
    {sub==='db'&&(<div>
      {/* ── Alertes malaise/détresse — priorité absolue, en tête du dashboard ── */}
      {(alertesSante||[]).filter(a=>a.statut!=='traitee').length>0&&(
        <div style={{background:'#fde0e0',border:'2px solid var(--dg)',borderRadius:12,padding:'14px 18px',marginBottom:16}}>
          <div className="D" style={{fontSize:16,color:'var(--dg)',marginBottom:8}}>🆘 Alerte{(alertesSante||[]).filter(a=>a.statut!=='traitee').length>1?'s':''} malaise / détresse en cours</div>
          <div style={{display:'flex',flexDirection:'column',gap:8}}>
            {(alertesSante||[]).filter(a=>a.statut!=='traitee').sort((a,b)=>b.ts.localeCompare(a.ts)).map(a=>{
              const s=staff.find(x=>x.id===a.sid);
              const mapUrl=(a.lat&&a.lng)?`https://www.google.com/maps/dir/?api=1&destination=${a.lat},${a.lng}`:null;
              return(<div key={a.id} style={{background:'var(--wh)',borderRadius:8,padding:'10px 12px',display:'flex',justifyContent:'space-between',alignItems:'center',flexWrap:'wrap',gap:8}}>
                <div>
                  <div style={{fontWeight:700,fontSize:13}}>{a.nom||fname(s)||'?'} {a.role?`(${a.role})`:''}</div>
                  <div style={{fontSize:11,color:'var(--si)'}}>{ft(a.ts)} · {fd(a.ts.slice(0,10))}{!a.lat&&<span style={{fontStyle:'italic'}}> · position non disponible</span>}</div>
                </div>
                <div style={{display:'flex',gap:6}}>
                  {s?.phone&&<a href={`tel:${s.phone}`} className="btn bmn sm" style={{textDecoration:'none'}}>📞 Appeler</a>}
                  {mapUrl&&<a href={mapUrl} target="_blank" rel="noopener noreferrer" className="btn bgh sm" style={{textDecoration:'none'}}>🗺️ Localiser</a>}
                  <button onClick={()=>setAlertesSante(prev=>prev.map(x=>x.id===a.id?{...x,statut:'traitee'}:x))} className="btn bgh sm">✓ Traité</button>
                </div>
              </div>);
            })}
          </div>
        </div>
      )}      {/* ── Centre de pilotage canicule ── */}
      <CaniPilotage/>
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(170px,1fr))',gap:12,marginBottom:20}}>
        {[
          {l:'Messages non lus',    v:unread,              c:unread>0?'var(--or)':'var(--ok)',  e:'💬', a:unread>0,              onClick:()=>setSub('msg')},
          {l:'Pointages manquants', v:lateCl.length,       c:lateCl.length>0?'var(--dg)':'var(--ok)',e:'⏰',a:lateCl.length>0, onClick:()=>onNav('rh')},
          {l:'Tâches non affectées',v:unassT,              c:unassT>0?'var(--pk)':'var(--ok)',  e:'📋', a:unassT>0,              onClick:()=>setSub('plan')},
          {l:'Demandes en attente', v:pendReq,             c:pendReq>0?'var(--or)':'var(--ok)', e:'📥', a:pendReq>0,             onClick:()=>{}},
          {l:'Rapports soumis',     v:rep?.filter(r=>r.sub).length||0, c:'var(--mn)',           e:'✅', a:false,                  onClick:()=>onNav('ctrl')},
          {l:'Commentaires non lus',v:nonLusCom, c:nonLusCom>0?comColor:'var(--ok)', e:'💬', a:nonLusCom>0, onClick:()=>setSub('com')},
        ].map((k,i)=>(
          <div key={i} className="scard" onClick={k.onClick} style={{borderTop:`3px solid ${k.c}`,cursor:'pointer',transition:'transform .15s,box-shadow .15s'}}
            onMouseEnter={e=>{e.currentTarget.style.transform='translateY(-2px)';e.currentTarget.style.boxShadow='0 6px 20px rgba(42,37,32,.1)';}}
            onMouseLeave={e=>{e.currentTarget.style.transform='';e.currentTarget.style.boxShadow='';}}>
            <div style={{fontSize:22,marginBottom:5}}>{k.e}</div>
            <div style={{fontSize:10,textTransform:'uppercase',letterSpacing:'.06em',color:'var(--si)',marginBottom:3}}>{k.l}</div>
            <div className="D" style={{fontSize:28,color:k.c}}>{k.v}</div>
            {k.a&&k.v>0&&<div style={{fontSize:10,color:k.c,fontWeight:600,marginTop:3}}>⚠ Attention</div>}
            <div style={{fontSize:9,color:'var(--si)',marginTop:4,opacity:.6}}>→ Voir</div>
          </div>
        ))}
      </div>
      {lateCl.length>0&&<div style={{background:'#fde0e0',border:'1.5px solid var(--dg)',borderRadius:10,padding:'10px 14px',marginBottom:12}}><div style={{fontWeight:700,fontSize:12,marginBottom:4}}>⏰ Pointages manquants</div>{lateCl.map(s=><div key={s.id} style={{fontSize:11,color:'var(--dg)',fontWeight:600}}>• {fname(s)}</div>)}</div>}
      {unassT>0&&<div style={{background:'#ffe0c0',border:'1.5px solid var(--or)',borderRadius:10,padding:'10px 14px',marginBottom:12}}><div style={{fontWeight:700,fontSize:12}}>📋 {unassT} tâche{unassT>1?'s':''} non affectée{unassT>1?'s':''} aujourd'hui</div></div>}
      {/* Ménages guidés (mode formation obligatoire) restés ouverts : Début pointé, Fin jamais pointée —
          le salarié a probablement fermé le guide avant la fin. Pas de blocage côté salarié (voir HANDOFF),
          juste une alerte ici pour que Claire/Franck sachent qu'une intervention est restée en suspens. */}
      {(()=>{
        const guideOuverts=st.filter(t=>{
          if(!['full','quick','linge','correction'].includes(t.type))return false;
          if(guideImposition(staff.find(s=>s.id===t.sid))!=='obligatoire')return false;
          const a=als.filter(x=>x.tid===t.id);
          return a.some(x=>x.type==='start')&&!a.some(x=>x.type==='end');
        });
        if(guideOuverts.length===0)return null;
        return(<div style={{background:'#ffe8d6',border:'1.5px solid var(--or)',borderRadius:10,padding:'10px 14px',marginBottom:12}}>
          <div style={{fontWeight:700,fontSize:12,marginBottom:4}}>🧭 {guideOuverts.length} ménage{guideOuverts.length>1?'s':''} guidé{guideOuverts.length>1?'s':''} resté{guideOuverts.length>1?'s':''} ouvert{guideOuverts.length>1?'s':''} (formation)</div>
          <div style={{fontSize:11,color:'#7a5a00',marginBottom:6}}>Début pointé, jamais terminé — probablement fermé en cours de route.</div>
          <div style={{display:'flex',flexDirection:'column',gap:2}}>
            {guideOuverts.map(t=>{
              const s=staff.find(x=>x.id===t.sid),b=biens.find(x=>x.id===t.bid);
              const ls=als.find(x=>x.tid===t.id&&x.type==='start');
              return<div key={t.id} style={{fontSize:11,color:'#7a5a00',fontWeight:600}}>• {fname(s)} · {b?.name||'?'} · débuté {ft(ls?.ts)} le {fd(t.date)}</div>;
            })}
          </div>
        </div>);
      })()}
      {/* Rappels périodiques sur items facturables (v145) — pense-bête générique, ex. Delphine
          Thomas / vérification vaisselle-placards, mais utilisable pour n'importe quel item
          facturable marqué "rappelOn" sur n'importe quel bien. Repose sur rep7, aucune donnée
          de suivi dédiée. Objectif de Claire : ne pas oublier, ne pas faire deux fois.
          v148 : case "Programmé" cochable directement ici (retour de Claire — rouvrir la fiche
          complète du bien à chaque fois n'a pas de sens pour un geste aussi fréquent). Lecture-
          avant-écriture sur biens8 comme saveB (SecProp.jsx), pour ne pas écraser une modification
          concurrente sur un autre champ du même bien pendant que le bandeau est ouvert. */}
      {(()=>{
        const enRetard=rappelsBfEnRetard(biens,rep,todayS);
        if(enRetard.length===0)return null;
        const nbRetard=enRetard.filter(x=>x.statut==='retard').length;
        const nbProgramme=enRetard.filter(x=>x.statut==='programme').length;
        // Non programmés d'abord (plus urgent), programmés ensuite (déjà pris en charge).
        const tries=[...enRetard].sort((a,b)=>(a.statut==='retard'?0:1)-(b.statut==='retard'?0:1));
        const toggleProgramme=async(bienId,fieldId,freq,cocher)=>{
          const pk=cocher?periodeKey(freq,todayS):null;
          // Optimiste en local d'abord (retour visuel immédiat), corrigé si l'écriture échoue.
          const applyLocal=list=>list.map(b=>b.id!==bienId?b:{...b,bf:(b.bf||[]).map(fld=>fld.id!==fieldId?fld:{...fld,rappelProgrammePeriode:pk})});
          setBiens(applyLocal);
          try{
            const current=await supaGet('biens8')||[];
            const updated=applyLocal(current);
            await supaSet('biens8',updated);
            setBiens(updated);
          }catch(e){
            console.error('[toggleProgramme] écriture biens8 échouée',e.message);
          }
        };
        return(<div style={{background:'#fff4e0',border:'1.5px solid #e5a83a',borderRadius:10,padding:'10px 14px',marginBottom:12}}>
          <div style={{fontWeight:700,fontSize:12,marginBottom:4,color:'#8a5a00'}}>
            🔔 {enRetard.length} rappel{enRetard.length>1?'s':''} périodique{enRetard.length>1?'s':''} pas encore fait{enRetard.length>1?'s':''}
            {nbProgramme>0&&<span style={{fontWeight:400,opacity:.85}}> — {nbRetard} à programmer, {nbProgramme} déjà programmé{nbProgramme>1?'s':''}</span>}
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:2}}>
            {tries.map(({bien,field,freq,derniere,statut})=>{
              const cli=clients.find(c=>c.id===bien.cid);
              const estProgramme=statut==='programme';
              return(<div key={bien.id+field.id} style={{fontSize:11,color:estProgramme?'#1e6fd6':'#8a5a00',fontWeight:600,display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
                <span>{estProgramme?'📌':'•'} {field.em||'💰'} {field.lb} — {bien.name}{cli?.name?` (${cli.name})`:''} · {RAPPEL_FREQS[freq]||freq}{estProgramme?' · programmé sur le planning':(derniere?` · dernière fois : ${fd(derniere.sub.slice(0,10))}`:' · jamais fait')}</span>
                <label style={{display:'flex',alignItems:'center',gap:4,cursor:'pointer',fontWeight:600}} title="Coche une fois la tâche posée sur le planning (Alerte plein écran)">
                  <input type="checkbox" checked={estProgramme} onChange={e=>toggleProgramme(bien.id,field.id,freq,e.target.checked)}/>
                  📌 Programmé
                </label>
                <button onClick={()=>setSub('plan')} className="btn bgh sm" style={{fontSize:10,padding:'2px 8px'}}>→ Planning</button>
              </div>);
            })}
          </div>
        </div>);
      })()}
      {/* Demandes propriétaires — pending + traitées des 7 derniers jours */}
      {(()=>{
        const cutoff=new Date(Date.now()-7*24*3600*1000).toISOString();
        const visibleReqs=P.req.filter(r=>r.st==='pending'||(r.st!=='pending'&&(r.updatedAt||r.createdAt||'')>=cutoff));
        if(visibleReqs.length===0)return null;
        const reqTypeLabel={checkin:'Check-in',checkout:'Check-out',menage:'Ménage',travaux:'Travaux',linge:'Linge',reclamation:'Réclamation',autre:'Autre'};
        const reqTypeToTaskType={checkin:'checkin',checkout:'checkout',menage:'full',travaux:'full',linge:'linge',reclamation:'full',autre:'full'};
        return(<div style={{marginBottom:16}}>
          <div className="D" style={{fontSize:17,marginBottom:10}}>📥 Demandes propriétaires ({pendReq} en attente)</div>
          <div style={{display:'flex',flexDirection:'column',gap:8}}>
            {visibleReqs.map(r=>{
              const b=biens.find(x=>x.id===r.bid);
              const c=clients.find(x=>x.id===r.cid);
              const alreadyCreated=pj.some(j=>j.fromReqId===r.id)||st.some(t=>t.fromReqId===r.id);
              const isPending=r.st==='pending';
              const borderColor=isPending?'var(--pk)':r.st==='approved'?'var(--ok)':'var(--dg)';
              return(<div key={r.id} className="card" style={{padding:'12px 15px',borderLeft:`3px solid ${borderColor}`,opacity:isPending?1:0.85}}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',flexWrap:'wrap',gap:10}}>
                  <div>
                    <div style={{display:'flex',gap:6,marginBottom:5,flexWrap:'wrap',alignItems:'center'}}>
                      <span className={`pill ${isPending?'pnd':r.st==='approved'?'pok':'pdg'}`}>{isPending?'En attente':r.st==='approved'?'Acceptée':'Refusée'}</span>
                      <span style={{fontWeight:700,fontSize:13}}>{b?.name}</span>
                      <span style={{fontSize:11,color:'var(--si)'}}>· {c?.name}</span>
                      {r.type==='reclamation'
                        ?<span style={{fontSize:11,background:'#fde8e8',color:'#b91c1c',border:'1px solid #f5b5b5',fontWeight:700,padding:'2px 7px',borderRadius:999}}>🛠 Réclamation</span>
                        :<span style={{fontSize:11,background:'var(--cr)',padding:'2px 7px',borderRadius:999}}>{reqTypeLabel[r.type]||r.type}</span>}
                    </div>
                    <div style={{fontSize:12,color:'var(--si)',marginBottom:4}}>{r.type==='reclamation'?'Signalée le':'Pour le'} {fd(r.rf)}</div>
                    {r.desc&&<div style={{fontSize:12}}>{r.desc}</div>}
                    {r.type==='menage'&&r.creneau&&r.creneau!=='anytime'&&<div style={{fontSize:11,background:'#fff7ed',border:'1px solid #fcd34d',borderRadius:6,padding:'4px 8px',marginTop:5,display:'inline-flex',alignItems:'center',gap:4}}>
                      🕐 {r.creneau==='from'?`Disponible à partir de ${r.heureDebut||'?'}`:r.creneau==='before'?`À faire avant ${r.heureFin||'?'}`:r.creneau}
                    </div>}
                    {r.type==='checkin'&&r.locataire&&(r.locataire.nom||r.locataire.tel||r.locataire.email)&&<div style={{fontSize:11,background:'#f0f9ff',border:'1px solid #7dd3fc',borderRadius:6,padding:'6px 10px',marginTop:6}}>
                      <div style={{fontWeight:700,marginBottom:3,color:'#0369a1'}}>👤 Locataire</div>
                      {r.locataire.nom&&<div>📛 {r.locataire.nom}</div>}
                      {r.locataire.tel&&<div>📞 <a href={`tel:${r.locataire.tel}`} style={{color:'var(--mn)',fontWeight:600}}>{r.locataire.tel}</a></div>}
                      {r.locataire.email&&<div>✉️ <a href={`mailto:${r.locataire.email}`} style={{color:'var(--mn)'}}>{r.locataire.email}</a></div>}
                    </div>}
                    {alreadyCreated&&<div style={{fontSize:11,color:'var(--ok)',fontWeight:600,marginTop:4}}>✓ Tâche créée dans le planning</div>}
                  </div>
                  {isPending&&<div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
                    {!alreadyCreated&&<button onClick={()=>{
                      const taskType=reqTypeToTaskType[r.type]||'full';
                      const newTask={id:'t'+Date.now(),bid:r.bid,sid:null,type:taskType,date:r.rf,sh:9,sm:0,dm:(r.type==='checkin'||r.type==='checkout')?15:90,prio:'normal',notes:`📥 Demande ${c?.name} : ${r.desc||reqTypeLabel[r.type]||r.type}`,fromReqId:r.id};
                      P.setSt([...st,newTask]);
                      P.setReq(P.req.map(x=>x.id===r.id?{...x,taskCreated:true,updatedAt:now()}:x));
                    }} className="btn bor sm">📋 Créer la tâche</button>}
                    <button onClick={()=>{
                      const tid=`client:${r.cid}`;
                      const msg={id:'m'+Date.now(),tid,ft:'admin',fid:'admin',ct:`Bonjour ${c?.name}, votre demande pour ${b?.name} le ${fd(r.rf)} a bien été prise en compte. Nous vous confirmons l'intervention.`,ts:now(),ra:true,ru:false};
                      P.setMsgs([...msgs,msg]);
                      P.setReq(P.req.map(x=>x.id===r.id?{...x,st:'approved',updatedAt:now()}:x));
                    }} className="btn bmn sm"><Ic n="ok" c="w-3.5 h-3.5"/>Accepter + répondre</button>
                    <button onClick={()=>{
                      const tid=`client:${r.cid}`;
                      const msg={id:'m'+Date.now(),tid,ft:'admin',fid:'admin',ct:`Bonjour ${c?.name}, votre demande pour ${b?.name} le ${fd(r.rf)} ne peut malheureusement pas être prise en compte à cette date. N'hésitez pas à nous contacter.`,ts:now(),ra:true,ru:false};
                      P.setMsgs([...msgs,msg]);
                      P.setReq(P.req.map(x=>x.id===r.id?{...x,st:'refused',updatedAt:now()}:x));
                    }} className="btn bgh sm">Refuser + répondre</button>
                  </div>}
                </div>
              </div>);
            })}
          </div>
        </div>);
      })()}
      <div className="D" style={{fontSize:17,marginTop:8,marginBottom:10}}>Tâches du jour</div>
      <div style={{overflowX:'auto'}}>
        <table className="tbl">
          <thead><tr><th>Bien</th><th>Type</th><th>Horaire</th><th>Assigné à</th><th>Statut</th></tr></thead>
          <tbody>
            {[...st.filter(t=>t.date===todayS),...pj.filter(j=>j.date===todayS)].length===0&&<tr><td colSpan={5} style={{textAlign:'center',color:'var(--si)',padding:'20px'}}>Aucune tâche</td></tr>}
            {st.filter(t=>t.date===todayS).sort((a,b)=>(a.sh-b.sh)||(a.sm-b.sm)).map(t=>{const b=biens.find(x=>x.id===t.bid),tt=TT[t.type]||{e:'❓',c:'#999',bg:'#eee',l:t.type||'Type inconnu'},s=staff.find(x=>x.id===t.sid),g=(P.gerants||[]).find(x=>x.id===t.sid);const assignee=s||g;const es=effSt(t,als);return(<tr key={t.id}><td><strong>{b?.name}</strong></td><td><span style={{background:tt.bg,color:tt.c,padding:'2px 7px',borderRadius:999,fontSize:11,fontWeight:600}}>{tt.e} {tt.l}</span></td><td>{String(t.sh).padStart(2,'0')}:{String(t.sm).padStart(2,'0')} ({t.dm}min)</td><td>{assignee?<span style={{color:g?'var(--or)':'var(--mn)',fontWeight:600}}>● {fname(assignee)}{g?' (direction)':''}</span>:<span style={{color:'var(--si)'}}>—</span>}</td><td>{t.risque&&!t.sid?<span style={{background:'#fff4d6',color:'#7a5a00',fontSize:11,fontWeight:700,padding:'3px 8px',borderRadius:999,border:'1px solid #e5c977'}}>⚠️ À risque</span>:<span className={`pill ${es==='done'?'pok':es==='progress'?'pwn':'pnd'}`}>{es==='done'?'Fait':es==='progress'?'En cours':'À faire'}</span>}</td></tr>);})}
            {pj.filter(j=>j.date===todayS).sort((a,b)=>(a.af||'').localeCompare(b.af||'')).map(j=>{const b=biens.find(x=>x.id===j.bid),p=prov.find(x=>x.id===j.pid);return(<tr key={j.id}><td><strong>{b?.name}</strong></td><td><span style={{background:'#ffe9d6',color:'var(--or)',padding:'2px 7px',borderRadius:999,fontSize:11,fontWeight:600}}>🏠 Mission</span></td><td>{j.af} → {j.db}</td><td>{p?<span style={{color:'var(--or)',fontWeight:600}}>● {p.name}</span>:<span style={{color:'var(--dg)',fontWeight:600}}>⚠ Non affecté</span>}</td><td>{j.risque&&!j.pid?<span style={{background:'#fff4d6',color:'#7a5a00',fontSize:11,fontWeight:700,padding:'3px 8px',borderRadius:999,border:'1px solid #e5c977'}}>⚠️ À risque</span>:<span className={`pill ${j.st==='done'?'pok':j.st==='assigned'?'pbl':'pnd'}`}>{j.st==='done'?'Fait':j.st==='assigned'?'Affecté':'À affecter'}</span>}</td></tr>);})}
          </tbody>
        </table>
      </div>
    </div>)}
    {sub==='plan'&&<WeekPlan arretsMaladie={P.arretsMaladie} st={st} setSt={P.setSt} pj={pj} setPj={P.setPj} staff={[...staff,...(P.gerants||[]).map(g=>({...g,_gerant:true}))]} prov={prov} biens={biens} leave={leave} unavail={unavail} als={als} rep={rep} clients={clients} sejours={P.sejours||[]} statutAppts={P.statutAppts||{}} setStatutAppts={P.setStatutAppts} tc={P.tc||[]} cpSoldes={P.cpSoldes||[]} rhSettings={P.settings||{}}/>}
    {sub==='com'&&<CommentairesView commentaires={commentaires} setCommentaires={setCommentaires} biens={biens} staff={staff}/>}
    {sub==='msg'&&<AdminChat msgs={msgs} setMsgs={setMsgs} msgsLu={msgsLu} setMsgsLu={setMsgsLu} staff={staff} prov={prov} clients={clients} gerants={P.gerants||[]} st={st} pj={pj} biens={biens}/>}
  </div>);
}

// ══════════════ SECTION RH ══════════════
// Les helpers RH heures (rhWeekDays…rhMoisCumulStat) vivaient ici en double
// de SecRH.jsx, jamais appelés depuis ce fichier (dead code depuis
// l'extraction v88, vérifié par grep). Supprimés — source unique désormais
// dans utils.js (chantier contrats[], Étape 0, 02/08/2026).

export { AdminChat, VueGen }
